import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Package, Users, Shield, TrendingUp } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useCMSContent } from "@/hooks/useCMSContent";
import DOMPurify from 'dompurify';

const About = () => {
  const { t, language } = useLanguage();
  const { settings } = useSiteSettings();
  const { content, loading } = useCMSContent('about-page', language);

  // Helper function to sanitize HTML
  const sanitizeHTML = (html: string | null | undefined): string => {
    if (!html) return '';
    return DOMPurify.sanitize(html);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">{t('loading')}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const pageContent = content?.content || {};

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-primary bg-clip-text text-transparent">
            {pageContent.hero_title || t('about.hero_title', `À Propos de ${settings.site_name}`)}
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {pageContent.hero_description || t('about.hero_description')}
          </p>
        </div>

        {/* Mission Section */}
        <section className="mb-16">
          <div className="bg-card rounded-lg p-8 shadow-sm border">
            <h2 className="text-3xl font-bold mb-4">{pageContent.mission_title || t('about.mission_title')}</h2>
            <div className="text-lg text-muted-foreground leading-relaxed" 
                 dangerouslySetInnerHTML={{ __html: sanitizeHTML(pageContent.mission_content || `
              <p class="mb-4">${settings.site_name || 'Nazandoo'} est une plateforme innovante qui connecte acheteurs et vendeurs locaux 
              à travers un réseau de points relais sécurisés. Notre mission est de faciliter le commerce 
              local tout en garantissant la sécurité et la satisfaction de tous nos utilisateurs.</p>
              <p>Nous croyons en un commerce équitable, transparent et accessible à tous, où chaque transaction 
              contribue au développement de l'économie locale.</p>
            `) }} />
          </div>
        </section>

        {/* Values Grid */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">
            {pageContent.values_title || 'Nos Valeurs'}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {(pageContent.values || [
              {
                icon: "shield",
                title: "Sécurité",
                description: "Protection maximale pour toutes vos transactions avec notre système de paiement sécurisé et vérification des produits."
              },
              {
                icon: "users",
                title: "Communauté",
                description: "Une communauté active et solidaire qui favorise les échanges locaux et le commerce de proximité."
              },
              {
                icon: "package",
                title: "Fiabilité",
                description: "Un réseau de points relais vérifiés et un service client disponible pour garantir votre satisfaction."
              },
              {
                icon: "trending-up",
                title: "Innovation",
                description: "Des solutions technologiques modernes pour simplifier vos achats et ventes en ligne."
              }
            ]).map((value: any, index: number) => {
              const IconComponent = 
                value.icon === 'shield' ? Shield :
                value.icon === 'users' ? Users :
                value.icon === 'package' ? Package :
                TrendingUp;

              return (
                <div key={index} className="text-center">
                  <div className="flex justify-center mb-4">
                    <div className="h-16 w-16 rounded-full bg-gradient-primary flex items-center justify-center">
                      <IconComponent className="h-8 w-8 text-primary-foreground" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{value.title}</h3>
                  <p className="text-muted-foreground">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Stats Section */}
        <section className="bg-gradient-primary text-primary-foreground rounded-lg p-12">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold mb-2">
                {pageContent.stats?.vendors || '1000+'}
              </div>
              <div className="text-lg opacity-90">
                {pageContent.stats?.vendors_label || t('about.active_vendors')}
              </div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">
                {pageContent.stats?.customers || '10,000+'}
              </div>
              <div className="text-lg opacity-90">
                {pageContent.stats?.customers_label || t('about.satisfied_customers')}
              </div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">
                {pageContent.stats?.relays || '50+'}
              </div>
              <div className="text-lg opacity-90">
                {pageContent.stats?.relays_label || t('about.relay_points')}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default About;
